import { ReactNode, useEffect, useMemo, useState } from 'react';
import { ButtonText } from '@motili/ui';
import Config from 'Config';
import { IconButton } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useAdvancedFilters } from 'common/context/DataGridAdvancedFilterContext';
import { EllipsisTooltip } from 'common/components/EllipsisTooltip';
import styles from './DataGridAdvancedFiltersMenu.module.scss';

const dataTestIdPrefix = 'data-grid-advanced-filters-menu-section';

const DataGridAdvancedFiltersMenuSection = ({
    children,
    id,
    title,
    advancedFilterKeys,
    clearSectionFilters,
    allowClearSectionFilters = true,
    countOverride,
}: {
    children: ReactNode;
    id: string;
    title: string;
    advancedFilterKeys: string[];
    clearSectionFilters?: () => void;
    allowClearSectionFilters?: boolean;
    countOverride?: number | null;
}) => {
    const {
        advancedFilters,
        setTotalAdvancedFilterCount,
        sectionExpansionStatus,
        setSectionExpansionStatus,
        clearAllAdvancedFilterId,
    } = useAdvancedFilters();

    // Bumped on every "Clear Filter" click for THIS section. Combined with
    // clearAllAdvancedFilterId (which changes on every "Clear All Filters"
    // click) into a `key` below, so this section's fields fully remount on
    // either clear action — resetting any field-local UI state (e.g. a
    // DatePicker's own invalid/red-border flag) that isn't tied to its
    // value and so wouldn't otherwise notice a value-less "clear" (e.g. a
    // field that was left showing an invalid typed draft that was never
    // successfully committed in the first place).
    const [sectionClearCount, setSectionClearCount] = useState(0);
    const fieldsResetKey = `${clearAllAdvancedFilterId ?? 'no-grid'}-${sectionClearCount}`;

    const isExpanded = useMemo(() => {
        return sectionExpansionStatus[id] ?? true;
    }, [sectionExpansionStatus, id]);

    const selectedFilterCount = useMemo(() => {
        if (countOverride !== undefined) {
            return countOverride || null;
        }
        let count = 0;
        Object.entries(advancedFilters).forEach(([key, value]) => {
            if (!advancedFilterKeys.includes(key)) return;

            if (Array.isArray(value)) {
                count += value.length;
            } else if (typeof value === 'object' && value !== null) {
                count += 1;
            }
        });
        return count || null;
    }, [advancedFilters, advancedFilterKeys, countOverride]);

    useEffect(() => {
        setSectionExpansionStatus(prevState => {
            return { ...prevState, [id]: true };
        });
    }, [id, setSectionExpansionStatus]);

    useEffect(() => {
        setTotalAdvancedFilterCount(prevState => {
            const newCount = selectedFilterCount || 0;
            return { ...prevState, [id]: newCount };
        });
    }, [selectedFilterCount, id, setTotalAdvancedFilterCount]);

    const disabledClearFilter =
        !selectedFilterCount || !allowClearSectionFilters;
    return (
        <div className={styles.filterMenuSectionContainer}>
            <span className={styles.filterMenuSection}>
                <h5 className={styles.filterMenuSectionTitle}>
                    <EllipsisTooltip
                        text={title}
                        style={{ marginRight: '4px' }}
                    />
                    <span style={{ color: Config.color.mediumGray }}>
                        {selectedFilterCount}
                    </span>
                </h5>
                <div style={{ whiteSpace: 'nowrap' }}>
                    {clearSectionFilters && (
                        <ButtonText
                            dataTestId={`${dataTestIdPrefix}-${id}-clear-filters`}
                            onClick={() => {
                                setSectionClearCount(count => count + 1);
                                clearSectionFilters();
                            }}
                            buttonStyle={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: `${
                                    !disabledClearFilter
                                        ? Config.color.motiliLightBlue
                                        : Config.color.mediumGray
                                }`,
                            }}
                            disabled={disabledClearFilter}
                        >
                            Clear Filter
                        </ButtonText>
                    )}
                    <IconButton
                        className={styles.muiIcon}
                        onClick={() =>
                            setSectionExpansionStatus(prevState => ({
                                ...prevState,
                                [id]: !prevState[id],
                            }))
                        }
                    >
                        {isExpanded ? (
                            <KeyboardArrowUpIcon
                                data-test-id={`${dataTestIdPrefix}-${id}-collapse-icon`}
                                fontSize='large'
                                htmlColor={Config.color.mediumGray}
                            />
                        ) : (
                            <KeyboardArrowDownIcon
                                data-test-id={`${dataTestIdPrefix}-${id}-expand-icon`}
                                fontSize='large'
                                htmlColor={Config.color.mediumGray}
                            />
                        )}
                    </IconButton>
                </div>
            </span>
            {isExpanded && (
                <div
                    key={fieldsResetKey}
                    className={styles.filterMenuSectionContent}
                >
                    {children}
                </div>
            )}
        </div>
    );
};

export default DataGridAdvancedFiltersMenuSection;
